import { useEffect, useState, JSX } from "react"
import styled from "styled-components"
import { Popconfirm, Input, Button, Tooltip, Divider, Select, Dropdown, TreeSelect, Typography, Switch, InputNumber, DatePicker } from "antd"
import {
    PlusCircleFilled,
    FilterOutlined,
    DeleteOutlined,
    ReloadOutlined,
    RollbackOutlined,
    UserOutlined,
    CheckCircleFilled,
    CheckOutlined,
    EllipsisOutlined,
    QuestionCircleOutlined,
    EyeOutlined,
    LockOutlined,
    FormOutlined,
} from "@ant-design/icons"
import { DefaultOptionType } from "antd/es/select"

import { Colors, Spaces, StyleHelpers } from "../../global"
import { Switcher } from "../../Switcher"
import { OptionItemWrapper } from "../FieldForm"
import AllowedFieldsTree from "./AllowedFieldsTree"
import type { User, FieldWithSelfAsSubfield, EntityField, Config, ConfigEntity, EntityType } from "../../../coreTypes/config"
import type { AccessFormType, EntityFormType, UserFormType } from "../../../machines/DesignerModel"
import { cleanInputNameStartingUpper, GetEntityAllFields } from "../../../helpers/functions"
import { ButtonGroupWrapper, SecondaryButton } from "../DataEntitiesTab/EntitiesFormWithFields"
import dayjs from "dayjs"

const { Option, OptGroup } = Select
const { TextArea } = Input

function flattenFieldRoute(fieldWithSub: FieldWithSelfAsSubfield) {
    const { fieldName, subfield } = fieldWithSub
    const result = [fieldName]
    if (subfield) result.push(...flattenFieldRoute(subfield))

    return result
}

function unwrapOption(
    field: EntityField,
    projectConfig: Config,
    currentLevel: number,
    limitLevels = 3,
    treeBranchParentUniqueEntities: string[] = [],
    validType?: string,
    parentPath?: string
) {
    const children: DefaultOptionType[] = []

    if (validType && ["INT", "FLOAT", "DOUBLE"].includes(field.type) && !["INT", "FLOAT", "DOUBLE"].includes(validType)) return
    if (validType && ["DATE", "TIME"].includes(field.type) && !["DATE", "TIME"].includes(validType)) return

    let newValue: string

    switch (field.type) {
        case "INT":
        case "FLOAT":
        case "DOUBLE":
        case "DATE":
        case "TIME":
        case "STRING":
        case "BOOLEAN":
        case "ENUM":
        case "USER_ID":
        case "OBJECT_ID":
        case "EMAIL":
        case "PHONENUMBER":
        case "FILE":
            if (
                validType &&
                field.type !== validType &&
                (!(["INT", "FLOAT", "DOUBLE"].includes(field.type) && ["INT", "FLOAT", "DOUBLE"].includes(validType)) ||
                    !(["DATE", "TIME"].includes(field.type) && ["DATE", "TIME"].includes(validType)))
            )
                return

            newValue = parentPath ? parentPath + "." + field.name + "*" + field.type : field.name + "*" + field.type

            return {
                value: newValue,
                key: newValue,
                label: (
                    <div style={{ display: "flex", flexDirection: "row", gap: Spaces.small }}>
                        <div style={{ color: "black" }}>{field.name}</div>
                        <i style={{ color: Colors.grayNormal }}>{field.type}</i>
                    </div>
                ),
            }
        case "SUBOBJECT":
            if (currentLevel >= limitLevels) break
            // Find subobject in projectConfig and process its fields as children
            const subobjectFields = projectConfig.subObjects?.[field.subObjectName!].fields
            if (!subobjectFields) break
            for (const f of subobjectFields) {
                if (f.connectionUnderlyingField) {
                    newValue = parentPath
                        ? parentPath + "." + field.name + "." + f.connectionUnderlyingField.name + "*" + f.connectionUnderlyingField.type
                        : field.name + "." + f.connectionUnderlyingField.name + "*" + f.connectionUnderlyingField.type

                    if (children.findIndex((c) => c.value == newValue) == -1)
                        children.push({
                            value: newValue,
                            key: newValue,
                            label: (
                                <div style={{ display: "flex", flexDirection: "row", gap: Spaces.small }}>
                                    <div style={{ color: "black" }}>{f.connectionUnderlyingField.name}</div>
                                    <i style={{ color: Colors.grayNormal }}>{f.connectionUnderlyingField.type}</i>
                                </div>
                            ),
                        })
                }

                const option = unwrapOption(
                    f,
                    projectConfig,
                    currentLevel + 1,
                    limitLevels,
                    treeBranchParentUniqueEntities,
                    undefined,
                    parentPath ? parentPath + "." + field.name : field.name
                )
                if (option && children.findIndex((c) => c.value == option.value) == -1) children.push(option)
            }

            newValue = parentPath ? parentPath + "." + field.name + "*" + field.type : field.name + "*" + field.type

            return {
                value: newValue,
                key: newValue,
                label: <div style={{ color: "black" }}>{field.name}</div>,
                selectable: false,
                disabled: true,
                children: children,
            }
        case "CONNECTION_BELONGSTO":
        case "CONNECTION_CONTAINS":
            if (currentLevel >= limitLevels) break
            // Find connected entity in projectConfig and process its fields as children
            if (treeBranchParentUniqueEntities.includes(field.connectedEntityName!)) break

            const connectedEntityFields = GetEntityAllFields(projectConfig, field.connectedEntityName!)
            if (!connectedEntityFields) break

            for (const f of connectedEntityFields) {
                if (f.connectionUnderlyingField) {
                    newValue = parentPath
                        ? parentPath + "." + field.name + "." + f.connectionUnderlyingField.name + "*" + f.connectionUnderlyingField.type
                        : field.name + "." + f.connectionUnderlyingField.name + "*" + f.connectionUnderlyingField.type

                    if (children.findIndex((c) => c.value == newValue) == -1)
                        children.push({
                            value: newValue,
                            key: newValue,
                            label: (
                                <div style={{ display: "flex", flexDirection: "row", gap: Spaces.small }}>
                                    <div style={{ color: "black" }}>{f.connectionUnderlyingField.name}</div>
                                    <i style={{ color: Colors.grayNormal }}>{f.connectionUnderlyingField.type}</i>
                                </div>
                            ),
                        })
                }

                const option = unwrapOption(
                    f,
                    projectConfig,
                    currentLevel + 1,
                    limitLevels,
                    treeBranchParentUniqueEntities.concat([field.connectedEntityName!]),
                    undefined,
                    parentPath ? parentPath + "." + field.name : field.name
                )
                if (option && children.findIndex((c) => c.value == option.value) == -1) children.push(option)
            }

            newValue = parentPath ? parentPath + "." + field.name + "*" + field.type : field.name + "*" + field.type

            return {
                value: newValue,
                key: newValue,
                label: <div style={{ color: "black" }}>{field.name}</div>,
                selectable: false,
                disabled: true,
                children: children,
            }
        case "USERSELF": // Not needed because it repeats the same object
            return
        case "EXTERNALENDPOINT":
        case "CUSTOMENDPOINT":
        default:
            return
    }
}

function getUserFields(userGroups: User[], accessForm: AccessFormType, projectConfig: Config, validType?: string) {
    const userTreeData: DefaultOptionType[] = []
    const selectedUserGroup = userGroups.find((userGroup) => userGroup.groupName == accessForm.accessItem.userGroupName)
    if (selectedUserGroup) {
        const fields = Object.keys(selectedUserGroup.userData.defaultFields).map(
            (name) =>
                ({
                    name,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    type: selectedUserGroup.userData.defaultFields[name],
                }) as EntityField
        )
        for (const uf of fields.concat(selectedUserGroup.userData.fields) || []) {
            if (uf.connectionUnderlyingField) {
                if (validType && uf.connectionUnderlyingField.type !== validType) continue
                const newValue = uf.connectionUnderlyingField.name + "*" + uf.connectionUnderlyingField.type
                if (userTreeData.findIndex((f) => f.value == newValue) == -1) {
                    userTreeData.push({
                        value: newValue,
                        key: newValue,
                        label: (
                            <div style={{ display: "flex", flexDirection: "row", gap: Spaces.small }}>
                                <div style={{ color: "black" }}>{uf.connectionUnderlyingField.name}</div>
                                <i style={{ color: Colors.grayNormal }}>{uf.connectionUnderlyingField.type}</i>
                            </div>
                        ),
                    })
                }
            }

            // TODO: allow more than one depth of nested fields in the future <- requires BE modification first
            const option = unwrapOption(uf, projectConfig, 0, 0, [], validType)
            if (option && userTreeData.findIndex((f) => f.value == option.value) == -1) userTreeData.push(option)
        }
    }

    return userTreeData
}

function isValidAccessItem(accessItem: AccessFormType["accessItem"], conditionsTypes: string[]): boolean {
    if (accessItem.rule?.type === "AND" && (!accessItem.rule?.name || !accessItem.rule?.andConditions || accessItem.rule?.andConditions.length === 0))
        return false

    if (accessItem.rule?.andConditions) {
        for (const ind in accessItem.rule?.andConditions) {
            const ac = accessItem.rule?.andConditions[ind]
            if (!ac.type && (!ac.objectFieldRoute.fieldName || !ac.userAccessValue)) return false
            if (ac.type && ["OBJECT_CONST", "USER_CONST"].includes(ac.type) && conditionsTypes[ind] !== "BOOLEAN" && !ac.constValue) return false
        }
    }

    if (accessItem.fields !== "ALL" && accessItem.fields.length === 0) return false

    return true
}

export interface AccessPermissionFormProps {
    userGroups: User[]
    dataEntities: ConfigEntity[]
    accessForm: AccessFormType
    addCondition: (type: "USER_CONST" | "OBJECT_CONST" | undefined) => void
    setUserGroupName: (groupName: string) => void
    projectConfig: Config
    userForm?: UserFormType
    entityForm?: EntityFormType
    switchRestriction: () => void
    selectAndConditionObjectField: (val: string, index: number) => void
    switchAccessFields: () => void
    changeFieldAccess: (fieldPath: string, accessType: string) => void
    changeAccessType: (accessType: "PUBLIC" | "AUTH" | "USERGROUP" | "APIKEY") => void
    sendDeleteAccessPermission: () => void
    sendResetAccessermission: () => void
    sendCancelAccessPermission: () => void
    sendSaveAccessPermission: () => void
    changeRuleName: (name: string) => void
    changeDescription: (description: string) => void
    setUserAccessValue: (val: string, index: number) => void
    deleteCondition: (index: number) => void
    setConstValue: (val: any, index: number) => void
}

export default function AccessPermissionForm(props: AccessPermissionFormProps) {
    const {
        userGroups,
        dataEntities,
        accessForm,
        addCondition,
        setUserGroupName,
        projectConfig,
        userForm,
        entityForm,
        switchRestriction,
        selectAndConditionObjectField,
        switchAccessFields,
        changeFieldAccess,
        changeAccessType,
        sendDeleteAccessPermission,
        sendResetAccessermission,
        sendCancelAccessPermission,
        sendSaveAccessPermission,
        changeRuleName,
        changeDescription,
        setUserAccessValue,
        deleteCondition,
        setConstValue,
    } = props
    const [isHovered, setIsHovered] = useState(false)
    const [ruleName, setRuleName] = useState(accessForm.accessItem.rule?.name)

    useEffect(() => {
        setRuleName(accessForm.accessItem.rule?.name)
    }, [accessForm.accessItem.rule?.name])

    const menuItems: any = []
    if (accessForm.originalAccessItem)
        menuItems.push({
            key: "delete",
            label: (
                <Popconfirm title="Are you sure you want to delete?" onConfirm={sendDeleteAccessPermission} okText="Yes" cancelText="No">
                    delete
                </Popconfirm>
            ),
            icon: <DeleteOutlined />,
        })

    if (accessForm.originalAccessItem && JSON.stringify(accessForm.originalAccessItem) !== JSON.stringify(accessForm.accessItem))
        menuItems.push({
            key: "reset",
            label: (
                <Popconfirm
                    title="Are you sure you want to reset without saving changes?"
                    onConfirm={sendResetAccessermission}
                    okText="Yes"
                    cancelText="No"
                >
                    reset
                </Popconfirm>
            ),
            icon: <ReloadOutlined />,
        })

    const menuItemAction = (menuItem: any) => {
        switch (menuItem.key) {
            case "delete":
                sendDeleteAccessPermission()
                break
            case "reset":
                sendResetAccessermission()
                break
        }
    }

    const localProjectConfig = {
        ...projectConfig,
    }
    const localDataEntities = JSON.parse(JSON.stringify(dataEntities)) as ConfigEntity[]
    const localUserGroups = JSON.parse(JSON.stringify(userGroups)) as User[]

    let topLevelFields: EntityField[] = []
    const objectTreeData: DefaultOptionType[] = []

    if (userForm) {
        const ugIndex = localUserGroups.findIndex((ug) => ug.groupName == userForm.originalUser?.groupName)
        if (ugIndex !== -1) {
            localUserGroups.splice(ugIndex, 1, userForm.user)
        } else if (userForm.originalUser) {
            localUserGroups.push(userForm.user)
        }
        localProjectConfig["users"] = JSON.parse(JSON.stringify(localUserGroups))

        const userDefaultFields = Object.keys(userForm.user.userData.defaultFields).map(
            (name) =>
                ({
                    name,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    type: userForm.user.userData.defaultFields[name],
                }) as EntityField
        )
        topLevelFields = userDefaultFields.concat(userForm.user.userData.fields)
        for (const uf of topLevelFields) {
            if (uf.connectionUnderlyingField) {
                const newValue = uf.connectionUnderlyingField.name + "*" + uf.connectionUnderlyingField.type
                if (objectTreeData.findIndex((f) => f.value == newValue) == -1) {
                    objectTreeData.push({
                        value: newValue,
                        key: newValue,
                        label: (
                            <div style={{ display: "flex", flexDirection: "row", gap: Spaces.small }}>
                                <div style={{ color: "black" }}>{uf.connectionUnderlyingField.name}</div>
                                <i style={{ color: Colors.grayNormal }}>{uf.connectionUnderlyingField.type}</i>
                            </div>
                        ),
                    })
                }
            }
            const option = unwrapOption(uf, localProjectConfig, 0, 4)
            if (option && objectTreeData.findIndex((f) => f.value == option.value) == -1) objectTreeData.push(option)
        }
    } else if (entityForm) {
        const eIndex = localDataEntities.findIndex((dataEntity) => dataEntity.entityName == entityForm.originalEntity?.entityName)
        if (eIndex !== -1) {
            localDataEntities.splice(eIndex, 1, entityForm.entity)
        } else if (entityForm.originalEntity) {
            localDataEntities.push(entityForm.entity)
        }
        localProjectConfig["entities"] = JSON.parse(JSON.stringify(localDataEntities))

        const entityDefaultFields = Object.keys(entityForm.entity.defaultFields).map(
            (name) =>
                ({
                    name,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    type: entityForm.entity.defaultFields[name],
                }) as EntityField
        )
        topLevelFields = entityDefaultFields.concat(entityForm.entity.fields)
        for (const ef of topLevelFields) {
            if (ef.connectionUnderlyingField) {
                const newValue = ef.connectionUnderlyingField.name + "*" + ef.connectionUnderlyingField.type
                if (objectTreeData.findIndex((f) => f.value == newValue) == -1) {
                    objectTreeData.push({
                        value: newValue,
                        key: newValue,
                        label: (
                            <div style={{ display: "flex", flexDirection: "row", gap: Spaces.small }}>
                                <div style={{ color: "black" }}>{ef.connectionUnderlyingField.name}</div>
                                <i style={{ color: Colors.grayNormal }}>{ef.connectionUnderlyingField.type}</i>
                            </div>
                        ),
                    })
                }
            }
            const option = unwrapOption(ef, localProjectConfig, 0, 4)
            if (option && objectTreeData.findIndex((f) => f.value == option.value) == -1) objectTreeData.push(option)
        }
    }

    const isAllFields = accessForm.accessItem.fields === "ALL"
    const allowedFieldsSwitcher = (
        <Switcher
            selected={!isAllFields}
            onClick={
                isAllFields
                    ? switchAccessFields
                    : () => {
                          // do nothing here
                      }
            }
        >
            {
                !isAllFields && <CheckCircleFilled style={{ fontSize: StyleHelpers.iconSize, color: Colors.background }} />
                // : (
                //     <CloseCircleOutlined style={{ fontSize: StyleHelpers.iconSize, color: Colors.primary }} />
                // )
            }
            {!isAllFields ? "limited fields allowed" : "Limit allowed fields"}
        </Switcher>
    )

    const isSavable = !accessForm.originalAccessItem || JSON.stringify(accessForm.originalAccessItem) !== JSON.stringify(accessForm.accessItem)

    let isPublicExists = false
    let isAuthExists = false
    if (userForm) {
        if (userForm.user.userData.access.findIndex((ac) => ac.type === "PUBLIC") !== -1) isPublicExists = true
        if (userForm.user.userData.access.findIndex((ac) => ac.type === "AUTH") !== -1) isAuthExists = true
    } else if (entityForm) {
        if (entityForm.entity.access.findIndex((ac) => ac.type === "PUBLIC") !== -1) isPublicExists = true
        if (entityForm.entity.access.findIndex((ac) => ac.type === "AUTH") !== -1) isAuthExists = true
    }

    return (
        <AccessPermissionFormWrapper isHovered={isHovered}>
            <AccessPermissionFormHeader>
                {/* empty div required as a spacer to align action buttons to the right side */}
                <div></div>
                <ButtonGroupWrapper>
                    {menuItems.length > 1 && (
                        <MoreWrapper>
                            <Dropdown
                                menu={{
                                    items: menuItems,
                                }}
                                placement="bottomRight"
                            >
                                <EllipsisOutlined style={{ fontSize: "20px", color: Colors.grayDark }} />
                            </Dropdown>
                        </MoreWrapper>
                    )}
                    {menuItems.length == 1 && !isSavable && (
                        <MoreWrapper style={{ margin: `0 ${Spaces.normal}` }}>
                            <Popconfirm
                                title="Are you sure you want to perform this action?"
                                onConfirm={() => {
                                    menuItemAction(menuItems[0])
                                }}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button type="text" shape="circle" icon={menuItems[0].icon} />
                            </Popconfirm>
                        </MoreWrapper>
                    )}

                    {isSavable && (
                        <MoreWrapper style={{ margin: `0 ${Spaces.normal}` }}>
                            <Popconfirm
                                title="Are you sure you want to cancel without saving changes?"
                                onConfirm={sendCancelAccessPermission}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button type="text" shape="circle" icon={<RollbackOutlined />} />
                            </Popconfirm>
                        </MoreWrapper>
                    )}

                    <SecondaryButton
                        isSolid={isSavable}
                        disabled={!isValidAccessItem(accessForm.accessItem, accessForm.conditionsTypes)}
                        type="primary"
                        shape="round"
                        icon={isSavable ? <CheckOutlined /> : <RollbackOutlined />}
                        onClick={sendSaveAccessPermission}
                    >
                        {isSavable ? "Save" : "Back"}
                    </SecondaryButton>
                </ButtonGroupWrapper>
            </AccessPermissionFormHeader>

            <AccessPermissionFormSubHeader>
                <MainSection>
                    <Select
                        placeholder="Select user group name..."
                        title="Select user group name..."
                        style={{ width: 225 }}
                        allowClear
                        value={
                            accessForm.accessItem.type === "PUBLIC"
                                ? "access_public"
                                : accessForm.accessItem.type === "AUTH"
                                  ? "access_auth"
                                  : accessForm.accessItem.userGroupName
                        }
                        onSelect={(value) => {
                            value === "access_public"
                                ? changeAccessType("PUBLIC")
                                : value === "access_auth"
                                  ? changeAccessType("AUTH")
                                  : setUserGroupName(value)
                        }}
                    >
                        <Option value="access_public" key="public" disabled={isPublicExists}>
                            <Tooltip
                                title={
                                    isPublicExists ? "Access permission rule for public access already exists. It can only be one per object." : ""
                                }
                            >
                                <OptionItemWrapper>
                                    <EyeOutlined style={{ fontSize: StyleHelpers.iconSize, color: Colors.grayDark }} />
                                    Public access
                                </OptionItemWrapper>
                            </Tooltip>
                        </Option>
                        <Option value="access_auth" key="auth" disabled={isAuthExists}>
                            <Tooltip
                                title={
                                    isAuthExists
                                        ? "Access permission rule for authenticated access already exists. It can only be one per object."
                                        : ""
                                }
                            >
                                <OptionItemWrapper>
                                    <LockOutlined style={{ fontSize: StyleHelpers.iconSize, color: Colors.grayDark }} />
                                    Authenticated access
                                </OptionItemWrapper>
                            </Tooltip>
                        </Option>

                        <OptGroup label="User group" key="user group">
                            {localUserGroups.map((e: User) => (
                                <Option value={e.groupName} key={e.groupName}>
                                    <OptionItemWrapper>
                                        <UserOutlined style={{ fontSize: StyleHelpers.iconSize, color: Colors.grayDark }} />
                                        {e.groupName}
                                    </OptionItemWrapper>
                                </Option>
                            ))}
                        </OptGroup>
                    </Select>
                    {/* TODO: add API key dropdown */}

                    <ExplanatoryText>
                        {["PUBLIC", "AUTH"].includes(accessForm.accessItem.type) ? " is allowed to" : " can access"}
                        {` object of type "${userForm?.user.groupName ?? entityForm?.entity.entityName ?? ""}" `}
                    </ExplanatoryText>
                </MainSection>
            </AccessPermissionFormSubHeader>

            <AccessPermissionFormBody>
                <Tooltip
                    mouseEnterDelay={0.4}
                    title={
                        accessForm.accessItem.rule?.type !== "ANY"
                            ? "(enabled) Limited the access to the entity by specifying conditions."
                            : "Enable to limit the access to the entity by specifying conditions."
                    }
                >
                    <Switcher selected={accessForm.accessItem.rule?.type !== "ANY"} onClick={switchRestriction}>
                        {
                            accessForm.accessItem.rule?.type !== "ANY" && (
                                <CheckCircleFilled style={{ fontSize: StyleHelpers.iconSize, color: Colors.background }} />
                            )
                            // : (
                            //     <CloseCircleOutlined style={{ fontSize: StyleHelpers.iconSize, color: Colors.primary }} />
                            // )
                        }
                        {accessForm.accessItem.rule?.type !== "ANY" ? "conditionally restricted" : "Restrict access conditionally"}
                    </Switcher>
                </Tooltip>

                {accessForm.accessItem.rule?.type !== "ANY" && (
                    <RestrictAccessForm>
                        <Input
                            placeholder="Condition name"
                            title="Condition name"
                            size="large"
                            style={{ width: "300px" }}
                            value={ruleName}
                            autoFocus={!accessForm.accessItem.rule?.name}
                            onBlur={(e) => {
                                changeRuleName(e.target.value)
                                setRuleName(cleanInputNameStartingUpper(e.target.value))
                            }}
                            onChange={(e) => {
                                setRuleName(e.target.value)
                            }}
                        />
                        <TextArea
                            placeholder="(optional) Corresponding description of the restriction condition..."
                            title="Corresponding description of the restriction condition..."
                            style={{ width: "500px" }}
                            value={accessForm.accessItem.rule?.description}
                            onChange={(e) => {
                                changeDescription(e.target.value)
                            }}
                            autoSize={{ minRows: 2, maxRows: 5 }}
                        />
                        <ConditionsTitle>
                            <FilterOutlined />
                            Conditions
                            <Tooltip
                                overlayInnerStyle={{ width: "600px" }}
                                title={
                                    `"Object field" – object is representing the single instance of the entity. "User field" – user who's trying to access an object. ` +
                                    `"Add matching condition" – condition to the rule where specified object field has to match the specified user field. ` +
                                    `"Object field constant value" – condition to the rule where specified object field has to match the constant value. ` +
                                    `"User field constant value" – condition to the rule where specified user field has to match the constant value. `
                                }
                            >
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </ConditionsTitle>

                        {accessForm.accessItem.rule?.andConditions?.map((ac, index) => {
                            let val: string | undefined = flattenFieldRoute(ac.objectFieldRoute).join(".")
                            if (val) {
                                val += "*" + accessForm.conditionsTypes[index]
                            } else {
                                val = undefined
                            }

                            // console.log(accessForm.conditionsTypes, ac)

                            let constValInput = <></>
                            const constDisabled = !val && (!accessForm.accessItem.userGroupName || ac.userAccessValue == "")

                            switch (accessForm.conditionsTypes[index]) {
                                case "BOOLEAN":
                                    constValInput = (
                                        <Switch
                                            checkedChildren="true"
                                            unCheckedChildren="false"
                                            disabled={constDisabled}
                                            checked={ac.constValue}
                                            onChange={(e) => setConstValue(e, index)}
                                        />
                                    )
                                    break
                                case "INT":
                                case "FLOAT":
                                case "DOUBLE":
                                    constValInput = (
                                        <InputNumber
                                            placeholder="Number const to match..."
                                            style={{ width: "210px" }}
                                            disabled={constDisabled}
                                            value={ac.constValue}
                                            onChange={(e) => setConstValue(e, index)}
                                        />
                                    )
                                    break
                                case "DATE":
                                    constValInput = (
                                        <DatePicker
                                            format="YYYY-MM-DD"
                                            placeholder={`Set date value...`}
                                            style={{ width: "50%" }}
                                            disabled={constDisabled}
                                            value={dayjs(ac.constValue, "YYYY-MM-DD")}
                                            showNow={false}
                                            onChange={(date) => {
                                                setConstValue(date?.toISOString() ?? "", index)
                                            }}
                                        />
                                    )
                                    break
                                case "TIME":
                                    constValInput = (
                                        <DatePicker
                                            format="YYYY-MM-DD HH:mm:ss"
                                            showTime={{}}
                                            placeholder="Set date and time value..."
                                            style={{ width: "50%" }}
                                            disabled={constDisabled}
                                            value={dayjs(ac.constValue, "YYYY-MM-DD HH:mm:ss")}
                                            showNow={false}
                                            onChange={(date) => {
                                                setConstValue(date?.toISOString() ?? "", index)
                                            }}
                                        />
                                    )
                                    break
                                case "STRING":
                                case "USER_ID":
                                case "OBJECT_ID":
                                case "EMAIL":
                                case "PHONENUMBER":
                                case "ENUM":
                                // FIXME: handle ENUM to show dropdown
                                // constValInput = <>Not supported yet</>
                                // break
                                default:
                                    constValInput = (
                                        <Input
                                            placeholder="Text const to match..."
                                            style={{ width: "210px" }}
                                            disabled={constDisabled}
                                            value={ac.constValue}
                                            onChange={(e) => {
                                                setConstValue(e.target.value, index)
                                            }}
                                        />
                                    )
                                    break
                            }

                            switch (ac.type) {
                                case "OBJECT_CONST":
                                    return (
                                        <ConditionWrapper key={index}>
                                            {index !== 0 && <>and</>}
                                            <> Object</>
                                            <TreeSelect
                                                showSearch
                                                treeLine={true}
                                                style={{ width: index !== 0 ? "212px" : "240px" }}
                                                value={val}
                                                dropdownStyle={{ maxHeight: 600, overflow: "auto" }}
                                                placeholder="Select Object field..."
                                                title="Select Object field..."
                                                allowClear
                                                treeDefaultExpandAll={val !== undefined}
                                                onChange={(val) => {
                                                    selectAndConditionObjectField(val, index)
                                                }}
                                                treeData={objectTreeData}
                                            />

                                            <span>=</span>

                                            {constValInput}

                                            {accessForm.accessItem.rule!.andConditions!.length > 1 && (
                                                <DeleteOutlined
                                                    style={{ color: Colors.primary }}
                                                    onClick={() => {
                                                        deleteCondition(index)
                                                    }}
                                                />
                                            )}
                                        </ConditionWrapper>
                                    )
                                case "USER_CONST":
                                    return (
                                        <ConditionWrapper key={index}>
                                            {index !== 0 && <>and</>}
                                            <> User</>
                                            <TreeSelect
                                                disabled={accessForm.accessItem.type === "USERGROUP" && !accessForm.accessItem.userGroupName}
                                                showSearch
                                                treeLine={true}
                                                style={{ width: index !== 0 ? "224px" : "252px" }}
                                                value={
                                                    accessForm.accessItem.userGroupName && ac.userAccessValue !== "" ? ac.userAccessValue : undefined
                                                }
                                                dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                                                placeholder="Select User field..."
                                                title="Select User field..."
                                                allowClear
                                                // treeDefaultExpandAll={ac.userAccessValue !== undefined}
                                                onChange={(val) => {
                                                    setUserAccessValue(val, index)
                                                }}
                                                treeData={getUserFields(localUserGroups, accessForm, localProjectConfig)}
                                            />

                                            <span>=</span>

                                            {constValInput}

                                            {accessForm.accessItem.rule!.andConditions!.length > 1 && (
                                                <DeleteOutlined
                                                    style={{ color: Colors.primary }}
                                                    onClick={() => {
                                                        deleteCondition(index)
                                                    }}
                                                />
                                            )}
                                        </ConditionWrapper>
                                    )
                                default:
                                    return (
                                        <ConditionWrapper key={index}>
                                            {index !== 0 && <>and</>}
                                            <TreeSelect
                                                showSearch
                                                treeLine={true}
                                                style={{ width: index !== 0 ? "259px" : "293px" }}
                                                value={val}
                                                dropdownStyle={{ maxHeight: 600, overflow: "auto" }}
                                                placeholder="Select Object field..."
                                                title="Select Object field..."
                                                allowClear
                                                treeDefaultExpandAll={val !== undefined}
                                                onChange={(val) => {
                                                    selectAndConditionObjectField(val, index)
                                                }}
                                                treeData={objectTreeData}
                                            />

                                            <span>=</span>

                                            <TreeSelect
                                                disabled={
                                                    !val || (accessForm.accessItem.type === "USERGROUP" && !accessForm.accessItem.userGroupName)
                                                }
                                                showSearch
                                                treeLine={true}
                                                style={{ width: "210px" }}
                                                value={
                                                    accessForm.accessItem.userGroupName && ac.userAccessValue !== "" ? ac.userAccessValue : undefined
                                                }
                                                dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                                                placeholder="Select User field..."
                                                title="Select User field..."
                                                allowClear
                                                onChange={(val) => {
                                                    setUserAccessValue(val, index)
                                                }}
                                                treeData={getUserFields(
                                                    localUserGroups,
                                                    accessForm,
                                                    localProjectConfig,
                                                    accessForm.conditionsTypes[index]
                                                )}
                                            />

                                            {accessForm.accessItem.rule!.andConditions!.length > 1 && (
                                                <DeleteOutlined
                                                    style={{ color: Colors.primary }}
                                                    onClick={() => {
                                                        deleteCondition(index)
                                                    }}
                                                />
                                            )}
                                        </ConditionWrapper>
                                    )
                            }
                        })}

                        {!["PUBLIC", "AUTH"].includes(accessForm.accessItem.type) ? (
                            <Dropdown.Button
                                type="primary"
                                menu={{
                                    items: [
                                        {
                                            key: "OBJECT_CONST",
                                            label: "Object field constant value",
                                        },
                                        {
                                            key: "USER_CONST",
                                            label: "User field constant value",
                                        },
                                    ],
                                    onClick: (e) => {
                                        addCondition(e.key as "USER_CONST" | "OBJECT_CONST" | undefined)
                                    },
                                }}
                                onClick={() => addCondition(undefined)}
                            >
                                {`Add matching condition`}
                            </Dropdown.Button>
                        ) : (
                            <Button type="primary" onClick={() => addCondition("OBJECT_CONST")}>
                                Object field constant value
                            </Button>
                        )}
                    </RestrictAccessForm>
                )}

                <Divider>Allowed fields</Divider>

                {!isAllFields ? (
                    <Popconfirm
                        placement="top"
                        title="Allow full access?"
                        description="Previously selected fields will be removed. And all fields will be allowed."
                        onConfirm={switchAccessFields}
                        // onCancel={() => {}}
                        okText="Allow all"
                        cancelText="Keep selected"
                    >
                        {allowedFieldsSwitcher}
                    </Popconfirm>
                ) : (
                    <Tooltip
                        placement="bottom"
                        overlayInnerStyle={{ width: "600px" }}
                        mouseEnterDelay={0.4}
                        title="If you want to limit the access to the entity for the above User group/API-key by specifying the allowed fields, select corresponding fields below. By default all fields allowed."
                    >
                        {allowedFieldsSwitcher}
                    </Tooltip>
                )}
                {!isAllFields && (
                    <AllowedFieldsTree
                        topLevelFields={topLevelFields}
                        projectConfig={localProjectConfig}
                        accessItem={accessForm.accessItem}
                        changeFieldAccess={changeFieldAccess}
                        restricted={["PUBLIC", "AUTH"].includes(accessForm.accessItem.type) ? (userForm ? ["READ"] : ["CREATE", "READ"]) : undefined}
                    />
                )}
            </AccessPermissionFormBody>
        </AccessPermissionFormWrapper>
    )
}

const AccessPermissionFormWrapper = styled.div<{ isHovered: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;

    padding: ${Spaces.normal} ${Spaces.normal} ${Spaces.large};
    /* background-color: ${(props) => (props.isHovered ? Colors.grayLight : "unset")}; */
    border-radius: ${StyleHelpers.radiusSmall};
`

const AccessPermissionFormHeader = styled.div`
    display: flex;
    direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    padding-left: ${Spaces.large};
`

const AccessPermissionFormSubHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spaces.normal};
    width: 100%;
    background-color: white;
    padding: ${Spaces.large} ${Spaces.normal};
    border-radius: ${StyleHelpers.radiusSmall};
    box-shadow: ${StyleHelpers.boldBoxShadow};
    margin-top: 15px;
    z-index: 2;
`

const MainSection = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${Spaces.normal};
`

const ExplanatoryText = styled.div`
    font-weight: 600;
    font-size: 15px;
`

const MoreWrapper = styled.div`
    flex-grow: 1;

    display: flex;
    flex-direction: row;
    justify-content: flex-end;
`

const AccessPermissionFormBody = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${Spaces.normal};
    align-items: flex-start;

    padding: ${Spaces.large} 0 90px;
    /* height: calc(100vh - 260px);
    overflow-y: auto; */
    scrollbar-width: none;
`

const RestrictAccessForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spaces.normal};
    align-items: flex-start;
    width: 100%;

    /* padding-left: ${Spaces.xLarge}; */
`

const ConditionsTitle = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${Spaces.small};

    font-size: 18px;
    font-weight: 600;
    color: ${Colors.grayDark};
`

const ConditionWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${Spaces.normal};

    /* padding-left: ${Spaces.xLarge}; */
`

const Selector = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0px;

    border-radius: 20px;
    border: 1px solid ${Colors.primary};
    cursor: pointer;
`

const SelectorOption = styled.div<{ selected: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 2px;

    font-size: 14px;
    padding: 3px ${Spaces.normal};
    border-radius: 20px;
    color: ${(props) => (props.selected ? Colors.background : Colors.primary)};
    background: ${(props) => (props.selected ? Colors.primary : Colors.background)};

    &:hover {
        background: ${(props) => (props.selected ? Colors.primaryHover : Colors.grayLight)};
    }
`

export { Selector, SelectorOption }
